import {
  CustomTooltip,
  StyledReactSelect,
} from '@fountain/fountain-ui-components';
import { Typography } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { components as reactSelectComponents } from 'react-select';
import type { MenuProps } from 'react-select/src/components/Menu';
import type { SingleValueProps } from 'react-select/src/components/SingleValue';

import { useStyles } from './styles';

export interface ConditionSelectOption {
  value: string;
  /**
   * Falls back to the value.
   *
   * A catalog field carries a label; an operator token and an `allowed_values`
   * entry do not always, and the stored token is a legible degradation where a
   * blank option is unpickable.
   */
  label?: string;
  /**
   * Listed but not pickable.
   *
   * The condition-type picker needs it: a type the server says cannot be
   * authored, or one a sibling row has already spent, has to stay visible
   * rather than vanish — an admin looking for it should still find it, even
   * where nothing on the panel says why. react-select honours `isDisabled` on
   * an option itself, so this only has to survive the mapping below.
   */
  isDisabled?: boolean;
}

export interface ConditionSelectProps {
  /** Lands on the input, so the row's controls stay individually addressable. */
  id: string;
  label: string;
  value: string;
  options: ConditionSelectOption[];
  onChange: (value: string) => void;
  /** The message for this control, once the admin has tried to save. */
  error?: string;
  /** A standing fact about the options, such as a list the server cut short. */
  note?: string;
  /** The control's place in the row, which the row decides rather than this. */
  className?: string;
}

/**
 * One picker in a condition row, on the design system's searchable select.
 *
 * Every list behind these is account-shaped: the field catalog is the account's
 * data keys minus the unsupported types, and a pick-list value is a data field's
 * own options. On the enterprise accounts this surface is built for that is
 * dozens to hundreds of entries, which a scroll-only menu handles worst of all —
 * so `isSearchable` is the point of the component and not a refinement.
 *
 * Configured the way `ComparatorSelect` and `DataKeySelect` in the workflow
 * editor configure the same components. Those two are not reused directly: both
 * mutate a workflow rule through `immer` and one reads the editor's context,
 * where a condition row here is driven by a draft and a callback. What carries
 * over is the configuration, not the wrapper.
 *
 * **On `StyledReactSelect` rather than the thinner `Dropdown` wrapper**, which
 * takes a string and looks the option up for you. Its lookup returns `undefined`
 * on a miss, and react-select reads an `undefined` `value` as *uncontrolled*:
 * `StateManager.getProp` falls back to `this.state.value`, which it writes on
 * every change and never from props. A cleared operand is exactly a miss — no
 * option has the value `''` — so the picker would go on displaying the choice
 * the admin made before the field changed under it, while the draft held
 * nothing. `null` is the only way to say "nothing is selected" to react-select,
 * and only a caller that resolves the option itself can pass it.
 *
 * Three deliberate departures from the two reference pickers:
 *
 * - `size="small"`. A row's other control is the typed value input, a 32px MUI
 *   input, and the design system's default `medium` select is 40px. One row
 *   holds both, and a row is not two densities.
 * - `tabSelectsValue={false}`. react-select otherwise commits the focused option
 *   on Tab, and opening a menu focuses the selected one — so looking at the
 *   field picker and tabbing away re-fires `onChange` with the field already
 *   chosen, and `withDataKey` clears the operator, value and compare key
 *   unconditionally. A MUI menu closes on Tab without selecting.
 * - No `required`. Every control in a row is required, so marking four of them
 *   per row with an asterisk says nothing the row does not already imply. What
 *   is missing is named on save instead, which is what `error` carries.
 */
export const ConditionSelect: React.FC<ConditionSelectProps> = ({
  id,
  label,
  value,
  options,
  onChange,
  error,
  note,
  className,
}) => {
  const styles = useStyles();

  // The control and everything under it, which is how this answers whether the
  // keyboard is still here. Read rather than compared against the input's id:
  // the id is a prop, and the component map below is built once.
  const fieldRef = React.useRef<HTMLDivElement | null>(null);

  // The box react-select draws the chosen option in. It is `overflow: hidden`
  // with an ellipsis, so it is the only element that can answer whether the
  // admin is reading the whole value or a prefix of it.
  const valueRef = React.useRef<HTMLDivElement | null>(null);

  /**
   * Whether the tooltip is showing the whole of a value the control is cutting.
   *
   * Measured when the tooltip would open rather than once on mount, which is
   * what `useConditionalTooltip` does. A condition row re-measures for three
   * reasons after mount — the admin picks a longer option, the type picker
   * rewrites which controls the row holds, and `conditionControls` rewraps the
   * line when the panel changes width — and a mount-time answer is stale in
   * exactly the cases this exists for.
   */
  const [showingFullValue, setShowingFullValue] = React.useState(false);

  /**
   * Whether the list is open, which is what silences the tooltip.
   *
   * Chrome matches `:focus-visible` on a text input whatever moved focus there,
   * so MUI's own keyboard gate lets a *click* through — and a click is what
   * opens the menu. Unsilenced, the tooltip lands on the menu's first row,
   * which is the option the control is already showing: opening the list to
   * change a long value would cover the line the admin is reading from.
   *
   * Read off the menu's own mount rather than `onMenuOpen` and `onMenuClose`:
   * react-select renders `Menu` only while the list is open, and the component
   * map below is built once, so one mount effect tracks it without two more
   * handlers.
   */
  const [listOpen, setListOpen] = React.useState(false);

  /**
   * Open the tooltip if, and only if, the control is cutting its value.
   *
   * MUI calls this on hover and on keyboard focus, and only on keyboard focus:
   * `Tooltip.handleFocus` gates on `isFocusVisible`. Focus lands on
   * react-select's input, a sibling of the value box, and reaches the tooltip's
   * own wrapper because React's `onFocus` bubbles.
   *
   * `requireFocus` is for the one caller MUI knows nothing about — the list
   * closing. A list dismissed by clicking elsewhere has already taken the
   * keyboard away, and a tooltip left behind on a control nobody is on would
   * be the only one on the panel with no pointer and no focus under it.
   *
   * Stable, because the component map below is built once and holds it. It
   * reads nothing but refs and setters, all of which are live through a frozen
   * closure.
   */
  const reveal = React.useCallback((requireFocus: boolean) => {
    const box = valueRef.current;
    const held =
      !requireFocus ||
      (fieldRef.current?.contains(document.activeElement) ?? false);

    setShowingFullValue(
      held && box !== null && box.scrollWidth > box.clientWidth,
    );
  }, []);

  // Built once. react-select rebuilds its component map from this object on
  // every render, so a type minted inside the render would remount the value
  // and drop the ref on each keystroke in the row's typed value input. The ref
  // and both setters are stable, which is what lets the map be.
  const selectComponents = React.useMemo(
    () => ({
      SingleValue: (props: SingleValueProps<ConditionSelectOption>) => (
        <reactSelectComponents.SingleValue
          {...props}
          // react-select spreads `innerProps` straight onto the div it draws,
          // so this is the one hook it offers onto that element.
          innerProps={{ ...props.innerProps, ref: valueRef }}
        />
      ),
      Menu: (props: MenuProps<ConditionSelectOption>) => {
        React.useEffect(() => {
          setListOpen(true);

          return () => setListOpen(false);
        }, []);

        return <reactSelectComponents.Menu {...props} />;
      },
    }),
    [],
  );

  // Derived from the control's own id, which the row makes unique per control,
  // so no two messages in a row share one. `-helper-text` is the suffix MUI
  // mints for a `TextField`'s helper text. The typed-value alternate of this
  // picker is a `TextField`, so the pair describes its input with one id.
  const errorId = `${id}-helper-text`;
  const noteId = `${id}-note`;
  const describedBy =
    [error ? errorId : undefined, note ? noteId : undefined].filter(Boolean).join(' ') || undefined;

  // Memoised on the caller's array: react-select compares `options` by identity
  // in `UNSAFE_componentWillReceiveProps` and takes a `setState` whenever it
  // changes, and the panel re-renders on every keystroke in the typed value
  // input beside this one.
  const resolved = React.useMemo(
    () =>
      options.map(option => ({
        value: option.value,
        label: option.label ?? option.value,
        isDisabled: option.isDisabled,
      })),
    [options],
  );

  const selected = resolved.find(option => option.value === value) ?? null;

  // The chosen option's own value, not the object. `resolved` is rebuilt
  // whenever the caller's `options` array is, and keying the measurement below
  // on the object would re-ask on a rebuild that changed nothing.
  const selectedValue = selected?.value ?? '';

  /**
   * Ask again when the list closes, and when the value changes under the
   * control.
   *
   * A closing list is not a focus event, so MUI never asks a second time: left
   * alone, an admin who picks a long key with Enter and tabs on would never see
   * the key they just set — the flow AC2 is written for. Asked here rather than
   * in the menu's own cleanup because the value and the list change in one
   * commit, and a measurement taken inside that commit reads the box before
   * react-select has drawn the new value into it.
   *
   * The value is here because a row clears its own controls: picking a field,
   * an attribute or an attribute source empties the controls after it
   * (`conditionForm`'s `withDataKey`, `withAttributeName`, `withAttributeScope`)
   * while leaving them mounted. A tooltip opened by hovering one of those is
   * answered by nothing when a sibling empties it — the list this control owns
   * never opened — so it went on showing a value the control no longer holds.
   *
   * Keyed on the chosen value rather than the option, so an options array
   * rebuilt for its own reasons does not re-ask.
   */
  React.useEffect(() => {
    if (listOpen) return;
    reveal(true);
  }, [listOpen, selectedValue, reveal]);

  return (
    <div className={clsx(styles.conditionSelect, className)} ref={fieldRef}>
      {/* Above the control, not below it: a condition row stacks its own value
          field, its checkbox and the next row under this one, and a tooltip
          opened downward covers the field the admin is about to reach.

          `open` is driven rather than left to the tooltip, because the answer
          depends on a measurement only this component can take — a value that
          fits needs no tooltip at all, and an account whose keys are short
          should never see one.

          `title` falls back to the control's own label. It is there because
          `CustomTooltip` returns its child bare on an empty title, so an unset
          picker would render one tree and a set one another — and React answers
          a changed element type by remounting the subtree. That remount lands on
          the keystroke that sets the value, so a keyboard admin who picks an
          option with Enter would have focus dropped to the document and tab from
          the top of the panel again. `open` requires a chosen option so that
          fallback can never be read as one: a control with nothing in it has
          nothing to cut, and the label it would show is already on screen
          above the control. */}
      <CustomTooltip
        dense
        onClose={() => setShowingFullValue(false)}
        onOpen={() => reveal(false)}
        open={showingFullValue && !listOpen && selected !== null}
        placement="top"
        title={selected?.label ?? label}
      >
        <StyledReactSelect
          inputId={id}
          label={label}
          // The design system sets `aria-labelledby` to the label *text* rather
          // than to an id, so it resolves to no element and the accessible name
          // falls through to `aria-label`. Passing both is what the workflow
          // editor's two pickers do, and it is the only thing naming the input —
          // several other selects in this app pass `label` alone and are unnamed.
          aria-label={label}
          // Reaches the input: react-select writes a fixed aria set onto it and
          // drops the rest, so `StyledReactSelect` clones the input child with
          // this and with `aria-invalid`.
          aria-describedby={describedBy}
          // Merged over the design system's own map rather than replacing it —
          // `StyledSelect` spreads a consumer's entries last, so every other
          // component it configures survives.
          components={
            selectComponents as unknown as Record<
              string,
              React.ComponentType<unknown>
            >
          }
          options={resolved}
          // Explicitly `null` rather than a missing option, per the note above.
          value={selected}
          onChange={(option?: ConditionSelectOption | null) =>
            onChange(option?.value ?? '')
          }
          // Boolean, not the message: the design system renders a string `error`
          // as a caption positioned 24px below the control, which on a row this
          // dense lands on the compare-with checkbox underneath. The border still
          // turns, and the message is rendered in flow below.
          error={Boolean(error)}
          isSearchable
          tabSelectsValue={false}
          // Empty rather than the design system's "Please select...", which is
          // neither free of an ellipsis nor of the politeness the voice rules cut.
          // The label already names the control, so an unset picker needs nothing
          // in the box.
          placeholder=""
          size="small"
        />
      </CustomTooltip>

      {/* `body2`, not `caption` or `subtitle1`: this theme leaves caption at
          14px unstyled and sets subtitle1 to 24px, so body2 is the size helper
          copy reads at here — the same variant the row's other explanatory line
          uses.

          `role="alert"` on top of the `aria-describedby` above, for the reason
          the scope group's message carries it: the message is mounted only once
          a save has been refused, so its appearance is the event to announce. A
          description is read when the input takes focus, and a refused save
          moves no focus. */}
      {note && (
        <Typography color="textSecondary" id={noteId} variant="body2">
          {note}
        </Typography>
      )}

      {error && (
        <Typography
          className={styles.conditionSelectError}
          color="error"
          id={errorId}
          role="alert"
          variant="body2"
        >
          {error}
        </Typography>
      )}
    </div>
  );
};
